import React, { Dispatch, useContext } from 'react';
import { Box } from '@mui/material';
import {
  InsertDriveFile as InsertDriveFileIcon,
  Person as PersonIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { useImmer } from 'use-immer';
import clsx from 'clsx';
import {
  EmploymentBasedCategory,
  FileData, FileType, ForeignNational, PersonType, UpdateFileDataInput, customGetForeignNationalFilesQuery, updateForeignNationalFilesMutation,
} from 'API';
import { updateFileData } from 'graphql/customFileQueries';
import FormCard from 'components/FormCard';
import { ALL_ROLES, MH_ROLES } from 'services/auth';
import DocumentService from 'services/document';
import { resolvers } from 'shared/enums';
import { prepEdits } from 'shared/utilities';
import { ALL_FILE_TYPES } from 'shared/constants';
import AlertContext from 'shared/contexts/AlertContext';
import { customGetForeignNationalFiles, updateForeignNationalFiles } from 'graphql/customPersonContainerQueries';
import ProfileContext from 'shared/contexts/ProfileContext';
import { format as fnsFormat } from 'date-fns';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';

const getUploader = (file: FileData) => {
  if (file.uploaderEmployer) { return 'uploaderEmployer'; }
  if (file.uploaderForeignNational) { return 'uploaderForeignNational'; }
  if (file.uploaderTeamMember) { return 'uploaderTeamMember'; }

  return 'uploader';
};

interface Props {
  className?: string;
  file: FileData;
  fileTypeOptions?: Array<FileType>;
  setOpen: Dispatch<boolean>;
}

interface FnPayload {
  id: string;
  eadExpDate?: string;
  I797ExpDate?: string;
  citizenshipEligibilityDate?: string | null | undefined;
}

interface FileExpType {
  expirationDate?: string | null | undefined;
  issueDate?: string | null | undefined
  fileType?: FileType | null | undefined;
  id: string;
}

const FileModalSidebar: React.FC<Props> = ({
  className, file, fileTypeOptions, setOpen,
}: Props) => {
  const [, setAlert] = useContext(AlertContext);
  const [profile, setProfile] = (useContext(
    ProfileContext,
  ) as unknown) as [EditablePerson, Dispatch<EditablePerson>, PersonType];

  const fields: Array<ToggleableField> = [
    {
      type: 'string',
      fieldName: 'fileName',
      canEdit: MH_ROLES,
      canView: MH_ROLES,
      icon: InsertDriveFileIcon,
      isRequired: true,
    },
    {
      type: 'select',
      fieldName: 'fileType',
      canEdit: MH_ROLES,
      canView: MH_ROLES,
      icon: InsertDriveFileIcon,
      isRequired: true,
      options: (fileTypeOptions || ALL_FILE_TYPES)
        .map((option) => ({
          value: option,
          label: resolvers.FileType(option as FileType),
        })),
      enumResolver: resolvers.FileType,
      config: {
        selectVariant: 'outlined',
      },
    },
    {
      type: 'avatar',
      fieldName: getUploader(file),
      canEdit: [],
      canView: MH_ROLES,
      icon: PersonIcon,
    },
    {
      type: 'date',
      fieldName: 'uploadDate',
      canEdit: [],
      canView: MH_ROLES,
      icon: TodayIcon,
    },
    {
      type: 'date',
      fieldName: 'expirationDate',
      canEdit: MH_ROLES,
      canView: MH_ROLES,
      icon: TodayIcon,
    },
    {
      type: 'date',
      fieldName: 'dueDate',
      canEdit: MH_ROLES,
      canView: MH_ROLES,
      icon: TodayIcon,
    },
    {
      type: 'date',
      fieldName: 'issueDate',
      canEdit: MH_ROLES,
      canView: MH_ROLES,
      icon: TodayIcon,
    },
  ];

  if (file.fileType === FileType.I_94) {
    fields.splice(5, 0,
      {
        type: 'boolean',
        fieldName: 'durationOfStatus',
        canEdit: MH_ROLES,
        canView: ALL_ROLES,
        icon: TodayIcon,
      });
  }

  const [
    edits, setEdits,
  ] = useImmer<UpdateFileDataInput>(
    prepEdits(file, fields) as UpdateFileDataInput,
  );

  const handleReset = () => {
    setEdits(prepEdits(file, fields) as UpdateFileDataInput);
  };

  const findLatestDate = (files: Array<FileExpType | null>) => {
    if (files?.length === 0) {
      return null;
    }

    let latestFile = files[0];
    // eslint-disable-next-line no-restricted-syntax
    for (const obj of files) {
      if (!!obj?.expirationDate && !!latestFile?.expirationDate && new Date(obj.expirationDate) > new Date(latestFile.expirationDate)) {
        latestFile = obj;
      }
    }

    return latestFile;
  };

  const handleSave = async () => {
    try {
      const input = {
        id: edits.id,
        expirationDate: edits.expirationDate,
        fileName: edits.fileName,
        fileType: edits.fileType,
        dueDate: edits.dueDate,
        issueDate: edits.issueDate,
        durationOfStatus: edits.durationOfStatus,
      };

      await client.graphql({
        query: updateFileData,
        variables: { input },
      });

      if (file?.ownerNationalID) {
        const res = await client.graphql({
          query: customGetForeignNationalFiles,
          variables: { id: file.ownerNationalID },
        }) as GraphQLResult<customGetForeignNationalFilesQuery>;
        const fnPayload: FnPayload = {
          id: file.ownerNationalID,
        };

        const { items: fnFiles } = res?.data?.getForeignNational?.files || {};
        if (fnFiles?.length) {
          const selectedTypeFiles = fnFiles.filter((currentFile) => (currentFile?.fileType === edits.fileType && currentFile?.id !== input.id));
          const latestExpFile = findLatestDate([...selectedTypeFiles, input]);

          if (!!latestExpFile?.expirationDate && latestExpFile?.fileType === FileType.EAD_CARD) fnPayload.eadExpDate = latestExpFile.expirationDate;
          if (!!latestExpFile?.expirationDate && latestExpFile?.fileType === FileType.I_797_APPROVAL_NOTICE) fnPayload.I797ExpDate = latestExpFile.expirationDate;
          if (!!latestExpFile?.issueDate && latestExpFile?.fileType === FileType.GREEN_CARD) {
            if (profile.hasApprovedI140.hasApproved) {
              let citizenshipEligibilityDate = fnsFormat(new Date(), 'yyyy-MM-dd');
              if (latestExpFile.issueDate) {
                const greenCardPriorityDate = latestExpFile.issueDate;
                if (greenCardPriorityDate) {
                  // Convert string date to date object
                  const dateObject = new Date(greenCardPriorityDate);
                  if (profile.hasApprovedI140.employmentBasedCategory === EmploymentBasedCategory.SPOUSE_OF_US_CITIZEN) {
                    // Adding 3 years in expiry date
                    const expiryDateWithThreeYears = dateObject.setFullYear(
                      dateObject.getFullYear() + 3,
                    );
                    const formatDate = new Date(expiryDateWithThreeYears);
                    const formattedDate = fnsFormat(formatDate, 'yyyy-MM-dd');
                    citizenshipEligibilityDate = formattedDate;
                  } else {
                    // Adding 5 years in expiry date
                    const expiryDateWithFiveYears = dateObject.setFullYear(
                      dateObject.getFullYear() + 5,
                    );
                    const formatDate = new Date(expiryDateWithFiveYears);
                    const formattedDate = fnsFormat(formatDate, 'yyyy-MM-dd');

                    citizenshipEligibilityDate = formattedDate;
                  }
                }
                fnPayload.citizenshipEligibilityDate = citizenshipEligibilityDate || null;
              }
            }
          }

          const response = (await client.graphql({
            query: updateForeignNationalFiles,
            variables: { input: fnPayload },
          })) as GraphQLResult<updateForeignNationalFilesMutation>;

          if (response.data?.updateForeignNational?.citizenshipEligibilityDate) {
            const { citizenshipEligibilityDate } = response.data?.updateForeignNational;
            setProfile((draft: ForeignNational) => {
              draft!.citizenshipEligibilityDate = citizenshipEligibilityDate;
            });
          }
        }
      }
      setOpen(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      setAlert(['Something went wrong saving this file.', 'error']);
    }
  };

  return (
    <Box className={clsx(className)}>
      <FormCard
        fields={fields}
        canEditForm
        labelGetter={DocumentService.getLabel}
        narrow
        singleColumn
        source={edits}
        setSource={setEdits}
        title="Document Information"
        resetEdits={handleReset}
        saveEdits={handleSave}
        type="fileData"
        isFormView
        useIcon
        actionsAtTop={false}
        actionsAtBottom
      />
    </Box>
  );
};

export default FileModalSidebar;
